import React, { useState } from "react";
import { getDateOptions } from "../constants/dates";
import { parseISODate, toISODate } from "../utils/dateUtils";
import { calDays, fmtDate, isSame, inRange } from "../utils/calendarHelpers";

export function DateRangePicker({
  dateRange,
  setDateRange,
  appliedCustomStart,
  setAppliedCustomStart,
  appliedCustomEnd,
  setAppliedCustomEnd,
  onTrack,
  style,
}) {
  const [showDatePicker, setDatePicker] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  function handleDayClick(d) {
    if (!customStart || (customStart && customEnd)) { setCustomStart(d); setCustomEnd(null); }
    else if (d < customStart) { setCustomEnd(customStart); setCustomStart(d); }
    else setCustomEnd(d);
  }

  return (
    <div style={{ position: "relative", flexShrink: 0, ...style }}>
      <button onClick={() => { setDatePicker(!showDatePicker); setShowCustom(false); }} style={{
        display: "flex", alignItems: "center", gap: 6,
        background: "#FAFAFA", border: "1px solid #CFCFCF", borderRadius: 8,
        padding: "7px 12px", cursor: "pointer", color: "#7b7b7b", fontSize: 12, fontWeight: 700,
        boxShadow: "0 3px 8px rgba(0,0,0,0.10)",
      }}>
        <span style={{ fontSize: 12 }}>📅</span>
        {dateRange === "Custom Range" && appliedCustomStart && appliedCustomEnd
          ? `${fmtDate(typeof appliedCustomStart === "string" ? parseISODate(appliedCustomStart) : appliedCustomStart)} – ${fmtDate(typeof appliedCustomEnd === "string" ? parseISODate(appliedCustomEnd) : appliedCustomEnd)}`
          : dateRange}
        <span style={{ color: "#7b7b7b", fontSize: 9 }}>▴</span>
      </button>

      {showDatePicker && !showCustom && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 10,
          overflow: "hidden", zIndex: 50, minWidth: 160, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        }}>
          {getDateOptions().map(opt => (
            <button key={opt} onClick={() => {
              if (opt === "Custom Range") {
                setShowCustom(true);
                if (appliedCustomStart && appliedCustomEnd) {
                  setCustomStart(parseISODate(appliedCustomStart));
                  setCustomEnd(parseISODate(appliedCustomEnd));
                  setCalMonth(parseISODate(appliedCustomStart));
                }
                return;
              }
              setDateRange(opt);
              setDatePicker(false);
              onTrack?.({ range: opt });
            }} style={{
              display: "block", width: "100%", textAlign: "left",
              padding: "10px 16px", background: dateRange === opt ? "#FFF5FB" : "transparent",
              border: "none", borderBottom: "1px solid #F0F0F0",
              color: dateRange === opt ? "#F90091" : "#3E3E3E",
              fontSize: 13, cursor: "pointer", fontWeight: dateRange === opt ? 600 : 400,
            }}>
              {opt}
              {dateRange === opt && <span style={{ float: "right", color: "#F90091" }}>✓</span>}
            </button>
          ))}
        </div>
      )}

      {showDatePicker && showCustom && (() => {
        const { firstDay, daysInMonth, year, m } = calDays(calMonth);
        const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
        const blanks = Array(firstDay).fill(null);
        const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
        return (
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0,
            background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 14,
            zIndex: 50, boxShadow: "0 12px 32px rgba(0,0,0,0.15)", padding: 16, width: 280,
          }}>
            <button onClick={() => setShowCustom(false)} style={{ background: "none", border: "none", color: "#626262", fontSize: 12, cursor: "pointer", padding: "0 0 10px", display: "flex", alignItems: "center", gap: 4 }}>← Presets</button>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <button onClick={() => setCalMonth(new Date(year, m - 1, 1))} style={{ background: "none", border: "none", color: "#626262", fontSize: 16, cursor: "pointer", padding: "2px 6px" }}>‹</button>
              <span style={{ color: "#151515", fontSize: 13, fontWeight: 600 }}>{MONTHS[m]} {year}</span>
              <button onClick={() => setCalMonth(new Date(year, m + 1, 1))} style={{ background: "none", border: "none", color: "#626262", fontSize: 16, cursor: "pointer", padding: "2px 6px" }}>›</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 4 }}>
              {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
                <div key={d} style={{ textAlign: "center", fontSize: 10, color: "#7b7b7b", padding: "2px 0" }}>{d}</div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
              {blanks.map((_, i) => <div key={`b${i}`} />)}
              {days.map(d => {
                const date = new Date(year, m, d);
                const isStart = isSame(date, customStart);
                const isEnd = isSame(date, customEnd);
                const inRng = inRange(date, customStart, customEnd);
                return (
                  <button key={d} onClick={() => handleDayClick(date)} style={{
                    textAlign: "center", fontSize: 12, padding: "5px 0", borderRadius: isStart || isEnd ? 6 : inRng ? 0 : 6,
                    background: isStart || isEnd ? "#F90091" : inRng ? "#FFE9F6" : "transparent",
                    color: isStart || isEnd ? "#fff" : inRng ? "#F90091" : "#3E3E3E",
                    border: "none", cursor: "pointer", fontWeight: isStart || isEnd ? 700 : 400,
                  }}>{d}</button>
                );
              })}
            </div>

            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #F0F0F0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#7b7b7b", marginBottom: 10 }}>
                <span>From: <span style={{ color: customStart ? "#151515" : "#CFCFCF" }}>{customStart ? fmtDate(customStart) : "—"}</span></span>
                <span>To: <span style={{ color: customEnd ? "#151515" : "#CFCFCF" }}>{customEnd ? fmtDate(customEnd) : "—"}</span></span>
              </div>
              <button
                disabled={!customStart || !customEnd}
                onClick={() => {
                  const start = toISODate(customStart);
                  const end = toISODate(customEnd);
                  setAppliedCustomStart(start);
                  setAppliedCustomEnd(end);
                  setDateRange("Custom Range");
                  setDatePicker(false);
                  setShowCustom(false);
                  onTrack?.({ range: "Custom Range", custom_start: start, custom_end: end });
                }}
                style={{
                  width: "100%", padding: "8px 0", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600,
                  cursor: customStart && customEnd ? "pointer" : "default",
                  background: customStart && customEnd ? "#F90091" : "#F0F0F0",
                  color: customStart && customEnd ? "#fff" : "#B8B8B8",
                }}>Apply Range</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
